import { commonRunEditState } from "./common_run_edit_state";
import { calculationFirstCellPrompt } from "./cell_explanation_prompt";

export type CommonCellContextV3 = Readonly<{
  version: "CommonCellContext.v3";
  surfaceId: "demand";
  measure: "quantity" | "revenue";
  cellKind: "period_leaf";
  period: Readonly<{
    field: "order_placed_month";
    grain: "month";
    startEpochMs: number;
    endExclusiveEpochMs: number;
  }>;
  dimensions: readonly [Readonly<{ field: "channel"; value: string }>];
  filters: readonly [];
  postFilters: readonly [];
  display:
    | Readonly<{
        value: string;
        unit: "units";
        rule: "math_round_whole";
      }>
    | Readonly<{
        value: string;
        unit: "USD";
        rule: "usd_accounting_whole";
      }>;
  state: Readonly<{
    basis: "base";
    edits: "none" | "saved";
    comparison: false;
  }>;
}>;

export type DemandCellContextInput = Readonly<{
  measure: unknown;
  periodField: unknown;
  monthStartEpochMs: unknown;
  segments: readonly unknown[] | undefined;
  row: Readonly<Record<string, unknown>>;
  filters: readonly unknown[] | undefined;
  postFilters: readonly unknown[] | undefined;
  rawValue: unknown;
  settingsNameAndVersion: unknown;
  comparison: unknown;
}>;

const POSITIVE_WHOLE_PATTERN = /^(?=.{1,38}$)[1-9][0-9]*$/;
const utf8ByteLength = (value: string): number => {
  let bytes = 0;
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit < 0x80) bytes += 1;
    else if (codeUnit < 0x800) bytes += 2;
    else if (
      codeUnit >= 0xd800 &&
      codeUnit <= 0xdbff &&
      index + 1 < value.length &&
      value.charCodeAt(index + 1) >= 0xdc00 &&
      value.charCodeAt(index + 1) <= 0xdfff
    ) {
      bytes += 4;
      index += 1;
    } else bytes += 3;
  }
  return bytes;
};

const boundedChannel = (value: unknown): string | undefined =>
  typeof value === "string" &&
  value.length > 0 &&
  value === value.trim() &&
  utf8ByteLength(value) <= 128 &&
  !/\p{Cc}/u.test(value)
    ? value
    : undefined;

const monthEndExclusive = (startEpochMs: unknown): number | undefined => {
  if (!Number.isSafeInteger(startEpochMs) || (startEpochMs as number) <= 0) {
    return undefined;
  }
  const start = new Date(startEpochMs as number);
  if (
    start.getUTCDate() !== 1 ||
    start.getUTCHours() !== 0 ||
    start.getUTCMinutes() !== 0 ||
    start.getUTCSeconds() !== 0 ||
    start.getUTCMilliseconds() !== 0
  ) {
    return undefined;
  }
  const end = Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1);
  return Number.isSafeInteger(end) && end > (startEpochMs as number)
    ? end
    : undefined;
};

const positiveWholeDisplay = (value: unknown): string | undefined => {
  if (typeof value === "bigint") {
    const numeric = Number(value);
    return Number.isSafeInteger(numeric) && numeric > 0
      ? String(numeric)
      : undefined;
  }
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return undefined;
  }
  const rounded = Math.round(value);
  return Number.isSafeInteger(rounded) && rounded > 0
    ? String(rounded)
    : undefined;
};

export function createDemandCellContext(
  input: DemandCellContextInput,
): CommonCellContextV3 | undefined {
  if (
    (input.measure !== "quantity" && input.measure !== "revenue") ||
    input.periodField !== "order_placed_month" ||
    !Array.isArray(input.segments) ||
    input.segments.length !== 1 ||
    input.segments[0] !== "channel" ||
    !Array.isArray(input.filters) ||
    input.filters.length !== 0 ||
    !Array.isArray(input.postFilters) ||
    input.postFilters.length !== 0 ||
    input.comparison !== false ||
    !input.row ||
    typeof input.row !== "object" ||
    Array.isArray(input.row)
  ) {
    return undefined;
  }

  const endExclusiveEpochMs = monthEndExclusive(input.monthStartEpochMs);
  const channel = boundedChannel(input.row.channel);
  const displayValue = positiveWholeDisplay(input.rawValue);
  if (!endExclusiveEpochMs || !channel || !displayValue) return undefined;

  const edits = commonRunEditState(input.settingsNameAndVersion);
  if (!edits) return undefined;
  const display =
    input.measure === "quantity"
      ? Object.freeze({
          value: displayValue,
          unit: "units" as const,
          rule: "math_round_whole" as const,
        })
      : Object.freeze({
          value: displayValue,
          unit: "USD" as const,
          rule: "usd_accounting_whole" as const,
        });

  return Object.freeze({
    version: "CommonCellContext.v3",
    surfaceId: "demand",
    measure: input.measure,
    cellKind: "period_leaf",
    period: Object.freeze({
      field: "order_placed_month",
      grain: "month",
      startEpochMs: input.monthStartEpochMs as number,
      endExclusiveEpochMs,
    }),
    dimensions: Object.freeze([
      Object.freeze({ field: "channel", value: channel }),
    ]) as readonly [Readonly<{ field: "channel"; value: string }>],
    filters: Object.freeze([]) as readonly [],
    postFilters: Object.freeze([]) as readonly [],
    display,
    state: Object.freeze({ basis: "base", edits, comparison: false }),
  });
}

export function isSupportedDemandCellRecipe(
  context: CommonCellContextV3,
): boolean {
  const channel = context.dimensions[0];
  const endExclusiveEpochMs = monthEndExclusive(context.period.startEpochMs);
  const displayMatchesMeasure =
    (context.measure === "quantity" &&
      context.display.unit === "units" &&
      context.display.rule === "math_round_whole") ||
    (context.measure === "revenue" &&
      context.display.unit === "USD" &&
      context.display.rule === "usd_accounting_whole");
  return (
    context.version === "CommonCellContext.v3" &&
    context.surfaceId === "demand" &&
    (context.measure === "quantity" || context.measure === "revenue") &&
    context.cellKind === "period_leaf" &&
    context.period.field === "order_placed_month" &&
    context.period.grain === "month" &&
    endExclusiveEpochMs === context.period.endExclusiveEpochMs &&
    context.dimensions.length === 1 &&
    channel?.field === "channel" &&
    boundedChannel(channel.value) === channel.value &&
    context.filters.length === 0 &&
    context.postFilters.length === 0 &&
    POSITIVE_WHOLE_PATTERN.test(context.display.value) &&
    displayMatchesMeasure &&
    context.state.basis === "base" &&
    (context.state.edits === "none" || context.state.edits === "saved") &&
    context.state.comparison === false
  );
}

export function demandCellPrompt(context: CommonCellContextV3): string {
  const channel = context.dimensions[0].value;
  const month = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(context.period.startEpochMs));
  const displayedValue = Number(context.display.value).toLocaleString("en-US");
  const measure = context.measure === "quantity" ? "demand" : "demand revenue";
  const value =
    context.display.unit === "units"
      ? `${displayedValue} units`
      : `$ ${displayedValue}`;
  return calculationFirstCellPrompt(
    `the ${channel} ${measure} of ${value} for ${month}`,
  );
}
